import * as React from 'react';
import {Mutation} from 'react-apollo';

import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import GoogleLogoSvg from 'shells/logos/google-g.svg';

import type {
    setGoogleMailSyncIsEnabled as setGoogleMailSyncIsEnabledMutation,
    setGoogleMailSyncIsEnabledVariables as setGoogleMailSyncIsEnabledMutationVariables,
    UserEmail as UserEmailFragment,
} from 'nutshell-graphql-types';

import {authToService} from '../../integrations/auth-to-service';
import {getAuthUrl} from '../../integrations/integration-mutation-response-helpers';

import {QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE} from '../constants';
import SET_GOOGLE_MAIL_SYNC_IS_ENABLED from '../graphql/mutations/set-google-mail-sync-is-enabled.graphql';

import {updateCacheFromMutation} from './update-cache-from-mutation';

const googleLogo = <GoogleLogoSvg width={24} />;
const windowFeatures = {
    toolbar: 'no',
    location: 'no',
    width: 600,
    height: 850,
} as const;

class GoogleEnableMutation extends Mutation<
    setGoogleMailSyncIsEnabledMutation,
    setGoogleMailSyncIsEnabledMutationVariables
> {}

type Props = {
    email: UserEmailFragment;
    onAuthStart: () => void;
    onAuthSuccess: (arg1?: React.ReactElement<any> | null | undefined) => Promise<any>;
    onAuthError: (arg1: string[]) => void;
    errors?: string[];
};

export function EmailWidgetGoogle(props: Props) {
    const [isAuthenticating, setIsAuthenticating] = React.useState<boolean>(false);

    return (
        <GoogleEnableMutation
            mutation={SET_GOOGLE_MAIL_SYNC_IS_ENABLED}
            update={updateCacheFromMutation}
            // @ts-expect-error
            refetchQueries={QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE}
        >
            {(enableGoogle, {loading: isMutationInFlight}) => {
                const hasErrors = props.errors && props.errors.length > 0;
                let status = props.email.isSyncEnabled ? 'enabled' : 'disabled';
                if (isMutationInFlight || isAuthenticating) {
                    status = props.email.isSyncEnabled ? 'disabling' : 'enabling';
                }

                const statusMessage = hasErrors
                    ? 'Needs attention'
                    : `Email sync is turned ${props.email.isSyncEnabled ? 'on' : 'off'}`;

                return (
                    <IntegrationWidget
                        key={props.email.id}
                        account={props.email.address}
                        // @ts-expect-error
                        status={status}
                        statusMessage={statusMessage}
                        errors={props.errors}
                        logo={googleLogo}
                        onConnectClick={() => {
                            enableGoogle({variables: {isEnabled: true}})
                                .then(getAuthUrl)
                                .then((authUrl) => {
                                    // If we get an authUrl back, then we need to auth
                                    // Highly unlikely in the case of google,
                                    // because we get all scopes up front.
                                    if (authUrl) {
                                        props.onAuthStart();
                                        setIsAuthenticating(true);
                                        authToService({
                                            authUrl,
                                            windowFeatures,
                                            authCheckUrl: '/google/has-auth',
                                        })
                                            .then(({hasAuth}) => {
                                                // If we're authed and requesting more scopes, this will come
                                                // back as true no matter what.  But handling it differently
                                                // isn't easy and the user experience isn't awful, we'll just go
                                                // back to the disabled state.
                                                if (hasAuth) {
                                                    props.onAuthSuccess().then(() => {
                                                        setIsAuthenticating(false);
                                                    });
                                                }
                                            })
                                            .catch((errMessages) => {
                                                props.onAuthError(errMessages);
                                                setIsAuthenticating(false);
                                            });
                                    }
                                });
                        }}
                        onDisconnectClick={() => {
                            enableGoogle({variables: {isEnabled: false}}).catch((error) => {
                                props.onAuthError(error);
                            });
                        }}
                        actionStyle='switch'
                    />
                );
            }}
        </GoogleEnableMutation>
    );
}
